import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/database';
import { Observable, of, BehaviorSubject } from 'rxjs';
import {
  switchMap,
  tap,
  first,
  mergeMap,
  map,
  debounceTime,
  startWith,
  distinctUntilChanged,
  shareReplay
} from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { groupBy, values, maxBy, orderBy, map as _map } from 'lodash';
import {
  Survey,
  Question,
  Option,
  ReplyItem,
  RepliesFilter,
  RepliesOrder,
  ForgeryStore,
  ForgedReply
} from '../../classes';
import { compareReplySnapshots } from '../../functions';
import * as _firebase from 'firebase/app';
const firebase = _firebase;

@Injectable({
  providedIn: 'root'
})
export class RepliesService {
  constructor(private db: AngularFireDatabase, private auth: AuthService) {}
  public raw: ReplyItem[] = [];
  public order = new RepliesOrder();
  public filter = new RepliesFilter();
  public store = new ForgeryStore();

  protected get grouped(): { [key: string]: ReplyItem[] } {
    return groupBy(this.raw, li => this.order.getGroupKeyValue(li)) as {
      [key: string]: ReplyItem[];
    };
  }
  protected get latest(): ReplyItem[] {
    // return values(this.grouped).map(list=>maxBy(list, 'timestamp'));
    return values(this.grouped).map(list => {
      // if admin has locked a row and is manipulating a row, do not change the row to the latest reply.
      const locked = list.find(
        li => (this.store.stores.get(li.id) ? true : false)
      );
      if (locked) return locked;
      else {
        /**
         * if the latest forged reply and the latest original reply are identical, return the original one
         */
        const latest = maxBy(list, 'timestamp') || list[0]; // if timestamp does not exists.
        const latestOriginal = maxBy(list.filter(li => li.forgeAlert !== true), 'timestamp');
        if (!latestOriginal) return latest;
        else if (latest.forgeAlert !== true) return latest;
        else {
          if (compareReplySnapshots(latestOriginal.snapshot, latest.snapshot) === true) return latestOriginal;
          else return latest;
        }
      }
    });
    // return this.raw;
  }
  private get filtered(): ReplyItem[] {
    return this.latest.filter(reply => this.filter.checkReply(reply));
  }
  private get ordered(): ReplyItem[] {
    return orderBy(
      this.filtered,
      li => this.order.getOrderKeyValue(li),
      this.order.orderDirection
    );
  }
  currentList: ReplyItem[];
  protected surveyIdChangeEvent = new BehaviorSubject<string>(null);
  public list: Observable<ReplyItem[]> = this.createListObservable().pipe(
    tap(() => (this.currentList = this.ordered)),
    map(() => this.currentList),
  );
  protected createListObservable () {
    return this.surveyIdChangeEvent.pipe(
      distinctUntilChanged(),
      switchMap(survey_id => {
        if (!survey_id) return of(null);
        else
          return this.getRepliesScope().pipe(
            switchMap(replyScope => this.getReplyMap(survey_id, replyScope)),
            mergeMap(reply_id => this.getReply(survey_id, reply_id)),
            tap(
              item =>
                !this.raw.find(li => li.id === item.id)
                  ? this.raw.push(item)
                  : null
            ),
            debounceTime(500),
            switchMap(() => this.filter.onChange.pipe(startWith(null))),
            switchMap(() => this.order.onChange.pipe(startWith(null))),
            switchMap(() => this.store.onChange.pipe(startWith(null))),
          );
      }),
      shareReplay(1)
    );
  }
  private getRepliesScope(): Observable<string> {
    return of(this.auth.getSurveyScope('replies') as string).pipe(
      map((scope: string) => (scope === '' ? 'all' : scope))
    );
  }
  private getReplyMap(
    survey_id: string,
    replyScope: string
  ): Observable<string> {
    return this.db
      .list(`replyMap/${survey_id}/${replyScope}`)
      .stateChanges(['child_added'])
      .pipe(map(ss => ss.key));
  }
  private getReply(survey_id: string, reply_id: string): Observable<ReplyItem> {
    return this.db
      .object<ReplyItem>(`reply/${survey_id}/${reply_id}`)
      .valueChanges()
      .pipe(
        first(),
        map(item => {
          return { ...item, id: reply_id };
        })
      );
  }
  watch(survey_id: string): Observable<ReplyItem[]> {
    this.surveyIdChangeEvent.next(survey_id);
    return this.list;
  }
  /**
   * get ForgeryStore store
   * @param reply target reply to forge
   * @param option target option to forge within forged reply
   */
  forgeGet(reply: ReplyItem): ForgedReply {
    return this.store.stores.get(reply.id);
  }
  /**
   * forge set
   * @param reply target reply to forge
   * @param option  target option to forge within forged reply
   * @param value null is only way to delete check.
   *
   */
  forgeSet(reply: ReplyItem, option: Option, value: any) {
    return this.store.setCheck(reply, option, value, this.db.createPushId());
  }
  forgeSetAs (reply:ReplyItem, as:string) {
    return this.store.setAs(reply, as);
  }
  forgeSave(survey: Survey): Promise<any> {
    console.info(`will be rewritten with observable in 1 by 1 action`);
    const survey_id = survey.id;
    const survey_version = survey.version;
    return Promise.all(
      Array.from(this.store.stores.values()).map(store => {
        // console.log({...store.forged, survey_version});
        return firebase.functions().httpsCallable('replyForge')({
          ...store.forged,
          survey_version,
          survey_id
        });
      })
    ).then(() => {
      this.store.empty();
    });
  }
  cancelChanges() {
    this.store.empty();
  }
  prettify(question: Question, reply: ReplyItem) {
    const store = this.store.stores.get(reply.id);
    if (store) return question.prettifyAnswer(store.forged);
    else return question.prettifyAnswer(reply);
  }
}
